import { useEffect, useState } from "react";
import { useGoals } from "@/hooks/useGoals";
import { useExamResults } from "@/hooks/useExams";

function daysUntil(dateStr: string): number | null {
  if (!dateStr) return null;
  const target = new Date(`${dateStr}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export default function HedeflerimPage() {
  const { goal, loading, error, reload, save } = useGoals();
  const { data: exams } = useExamResults();

  const [targetNet, setTargetNet] = useState<string>("");
  const [targetSchool, setTargetSchool] = useState<string>("");
  const [targetExamDate, setTargetExamDate] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading) {
      setTargetNet(goal.targetNet != null ? String(goal.targetNet) : "");
      setTargetSchool(goal.targetSchool);
      setTargetExamDate(goal.targetExamDate);
    }
  }, [goal, loading]);

  // Deneme verisi ilk render'da henüz yüklenmemiş olabilir. null durumda
  // .length çağırmak sayfayı düşürdüğü için güvenli bir boş liste kullanıyoruz.
  const examList = exams ?? [];
  const lastNet = examList.length > 0 ? examList[0].net : null;
  const daysLeft = daysUntil(targetExamDate);
  const netGoal = targetNet ? Number(targetNet) : null;
  const netGap = netGoal != null && lastNet != null ? netGoal - lastNet : null;
  const netProgress =
    netGoal != null && netGoal > 0 && lastNet != null
      ? Math.min(100, Math.round((lastNet / netGoal) * 100))
      : 0;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaveError(null);
    if (targetNet && (!Number.isFinite(Number(targetNet)) || Number(targetNet) < 0 || Number(targetNet) > 500)) {
      setSaveError("Hedef net 0 ile 500 arasında olmalı.");
      return;
    }
    setSaving(true);
    setSaved(false);
    const result = await save({
      targetNet: targetNet ? Number(targetNet) : null,
      targetSchool: targetSchool.trim(),
      targetExamDate: targetExamDate,
    });
    setSaving(false);
    if (result.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } else {
      setSaveError(result.message);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
          Hedeflerim
        </h1>
        <p className="text-sm md:text-base text-foreground-600 mt-1">
          Hedef netini ve sınav tarihini belirle, ilerlemeni takip et.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center justify-between gap-3">
                <span>{error}</span>
                <button type="button" onClick={reload} className="font-semibold underline whitespace-nowrap">Tekrar dene</button>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Hedef Net
              </label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={targetNet}
                onChange={(e) => setTargetNet(e.target.value)}
                placeholder="Örn: 95"
                className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <p className="mt-1 text-xs text-foreground-500">Hedeflediğin deneme neti.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Hedef Okul / Bölüm (isteğe bağlı)
              </label>
              <input
                type="text"
                value={targetSchool}
                onChange={(e) => setTargetSchool(e.target.value)}
                placeholder="Örn: Galatasaray Lisesi"
                className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Sınav Tarihi
              </label>
              <input
                type="date"
                value={targetExamDate}
                onChange={(e) => setTargetExamDate(e.target.value)}
                className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <p className="mt-1 text-xs text-foreground-500">
                LGS / YKS / KPSS gibi hedeflediğin sınavın tarihi.
              </p>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="whitespace-nowrap px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60"
            >
              {saving ? "Kaydediliyor…" : "Hedefi Kaydet"}
            </button>

            {saved && (
              <p className="text-sm text-primary-700 flex items-center gap-2">
                <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                Hedefin kaydedildi!
              </p>
            )}
            {saveError && (
              <p className="text-sm text-red-700 flex items-center gap-2" role="alert">
                <i className="ri-error-warning-line" aria-hidden="true"></i>
                {saveError}
              </p>
            )}
          </form>
        </div>

        {/* Özet / geri sayım */}
        <div className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="font-heading font-semibold text-base text-foreground-950">
              Sınav Geri Sayımı
            </h3>
            {daysLeft !== null && daysLeft >= 0 ? (
              <div className="mt-4 text-center">
                <p className="text-5xl font-heading font-bold text-accent-700">{daysLeft}</p>
                <p className="mt-1 text-sm text-foreground-600">gün kaldı</p>
                {targetExamDate && (
                  <p className="mt-2 text-xs text-foreground-500">
                    {new Date(`${targetExamDate}T00:00:00`).toLocaleDateString("tr-TR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                )}
              </div>
            ) : daysLeft !== null && daysLeft < 0 ? (
              <p className="mt-4 text-sm text-foreground-600 text-center">
                Sınav tarihi geçti. Yeni hedef tarihini güncelleyebilirsin.
              </p>
            ) : (
              <p className="mt-4 text-sm text-foreground-600 text-center">
                Sınav tarihini belirle, geri sayım başlasın.
              </p>
            )}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="font-heading font-semibold text-base text-foreground-950">
              Hedef Karşılaştırması
            </h3>
            {netGoal != null && lastNet != null ? (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-600">Son netin</span>
                  <span className="font-semibold text-foreground-950">{lastNet}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-sm">
                  <span className="text-foreground-600">Hedef net</span>
                  <span className="font-semibold text-primary-700">{netGoal}</span>
                </div>
                <div className="mt-3 h-2.5 w-full rounded-full bg-background-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary-500"
                    style={{ width: `${netProgress}%` }}
                  />
                </div>
                <p className="mt-3 text-sm text-foreground-800">
                  {netGap !== null && netGap > 0
                    ? `Hedefine ${netGap} net kaldı. Devam! 💪`
                    : "Hedefine ulaştın, harika! 🎉"}
                </p>
              </div>
            ) : (
              <p className="mt-4 text-sm text-foreground-600">
                Hedef netini belirle ve bir deneme çöz; aradaki farkı burada görelim.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
